import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { Prompt } from '../types';
import { publicPromptsApi, handleApiError, paymentApi } from '../services/api';
import { PROMPT_CATEGORIES } from '../utils/constants';
import UserCard from './UserCard';
import { userApi } from '../services/api';
import RelatedPrompts from './RelatedPrompts';
import GoogleAdBanner from './GoogleAdBanner';
import PaymentModal from './PaymentModal';
import ImageHoverPreview from './ImageHoverPreview';

interface PromptPlazaProps {
  onPromptCopied?: () => void;
}

interface PromptPlazaCardProps {
  prompt: Prompt;
  onCopy: (prompt: Prompt) => void;
  isAuthenticated: boolean;
  copyingStates: Record<number, 'idle' | 'loading' | 'success' | 'error'>;
  allPrompts?: Prompt[]; // 新增：所有提示词数据用于相关推荐
}

// 提示词详情弹框组件
interface PromptDetailModalProps {
  prompt: Prompt;
  isOpen: boolean;
  onClose: () => void;
  onCopy: (prompt: Prompt) => void;
  isAuthenticated: boolean;
  copyingStates: Record<number, 'idle' | 'loading' | 'success' | 'error'>;
  allPrompts?: Prompt[]; // 新增：所有提示词数据用于相关推荐
  onPromptSelect?: (prompt: Prompt) => void; // 新增：选择其他提示词的回调
}

const PromptDetailModal: React.FC<PromptDetailModalProps> = ({
  prompt,
  isOpen,
  onClose,
  onCopy,
  isAuthenticated,
  copyingStates,
  allPrompts = [], // 默认空数组
  onPromptSelect
}) => {
  const copyState = copyingStates[prompt.id] || 'idle';
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPurchased, setIsPurchased] = useState(prompt.purchased || false);
  
  // 检查购买状态
  useEffect(() => {
    const checkPurchaseStatus = async () => {
      if ((prompt.price || 0) > 0 && isAuthenticated && !isPurchased) {
        try {
          const result = await paymentApi.checkPurchase('prompt', prompt.id);
          if (result.success && result.data?.purchased) {
            setIsPurchased(true);
          }
        } catch (error) {
          console.error('检查购买状态失败:', error);
        }
      }
    };
    if (isOpen) {
      checkPurchaseStatus();
    }
  }, [prompt.id, prompt.price, isAuthenticated, isPurchased, isOpen]);
  
  // 是否需要付费
  const needPayment = (prompt.price || 0) > 0 && !isPurchased;

  const handleCopy = () => {
    if (!isAuthenticated) {
      toast.error('请先登录后再复制提示词');
      return;
    }
    // 如果需要付费且未购买，提示需要先购买
    if (needPayment) {
      toast.error('请先购买后再复制提示词');
      setShowPaymentModal(true);
      return;
    }
    onCopy(prompt);
  };
  
  // 支付成功回调
  const handlePaymentSuccess = () => {
    setIsPurchased(true);
    setShowPaymentModal(false);
    toast.success('购买成功！现在可以复制提示词了');
  };

  // 获取复制按钮的样式和内容
  const getCopyButtonConfig = () => {
    if (!isAuthenticated) {
      return {
        className: 'bg-gray-100 text-gray-400 cursor-not-allowed',
        disabled: true,
        icon: 'fa-copy',
        text: '请先登录'
      };
    }
    
    // 如果需要付费，显示付费按钮
    if (needPayment) {
      return {
        className: 'bg-emerald-500 text-white hover:bg-emerald-600',
        disabled: false,
        icon: 'fa-lock',
        text: `支付 ¥${(prompt.price || 0).toFixed(2)} 解锁`
      };
    }

    switch (copyState) {
      case 'loading':
        return {
          className: 'bg-blue-500 text-white cursor-wait',
          disabled: true,
          icon: 'fa-spinner fa-spin',
          text: '复制中...'
        };
      case 'success':
        return {
          className: 'bg-green-500 text-white',
          disabled: true,
          icon: 'fa-check',
          text: '复制成功'
        };
      case 'error':
        return {
          className: 'bg-red-500 text-white hover:bg-red-600',
          disabled: false,
          icon: 'fa-exclamation-triangle',
          text: '复制失败，重试'
        };
      default:
        return {
          className: 'bg-primary text-white hover:bg-primary-dark',
          disabled: false,
          icon: 'fa-copy',
          text: '复制到我的提示词'
        };
    }
  };

  const buttonConfig = getCopyButtonConfig();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden transition-colors duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 弹框头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                  {prompt.title}
                </h2>
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <i className="fas fa-user text-gray-400 dark:text-gray-500 mr-2"></i>
                  <span className="font-medium">{prompt.author || '匿名用户'}</span>
                  <span className="mx-2">•</span>
                  <span>{new Date(prompt.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="ml-4 p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            {/* 弹框内容 */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* 标签和状态 */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <i className="fas fa-globe text-green-600 mr-1"></i>
                  公开
                </span>
                {prompt.hotness !== undefined && prompt.hotness > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <i className="fas fa-fire text-orange-600 mr-1"></i>
                    {prompt.hotness}
                  </span>
                )}
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <i className="fas fa-folder text-blue-600 mr-1"></i>
                  {prompt.category || '未分类'}
                </span>
                {/* 价格标签 */}
                {prompt.price !== undefined && prompt.price > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                    <i className="fas fa-tag text-emerald-600 mr-1"></i>
                    ¥{prompt.price.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Tags */}
              {prompt.tags && prompt.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {prompt.tags.map((tag, index) => (
                    <span
                      key={`${prompt.id}-modal-tag-${index}-${tag}`}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* 完整内容 */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  <i className="fas fa-file-text text-primary mr-2"></i>
                  提示词内容
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <pre className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap font-sans">
                    {prompt.content}
                  </pre>
                </div>
              </div>

              {/* 效果展示图片 */}
              {prompt.images && prompt.images.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    <i className="fas fa-images text-primary mr-2"></i>
                    效果展示
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {prompt.images.map((imageUrl, index) => (
                      <a
                        key={index}
                        href={imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:opacity-90 transition-opacity"
                      >
                        <img 
                          src={imageUrl} 
                          alt={`效果图 ${index + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* 相关提示词推荐 */}
              {allPrompts.length > 1 && (
                <RelatedPrompts
                  currentPrompt={prompt}
                  allPrompts={allPrompts}
                  maxItems={4}
                  onPromptClick={(relatedPrompt) => {
                    if (onPromptSelect) {
                      onPromptSelect(relatedPrompt);
                    }
                  }}
                />
              )}
            </div>

            {/* 弹框底部 */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <i className="fas fa-clock mr-1"></i>
                创建于 {new Date(prompt.createdAt).toLocaleString()}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                >
                  关闭
                </button>
                <motion.button
                  onClick={handleCopy}
                  className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 relative overflow-hidden ${buttonConfig.className}`}
                  disabled={buttonConfig.disabled}
                  whileHover={!buttonConfig.disabled ? { scale: 1.02 } : {}}
                  whileTap={!buttonConfig.disabled ? { scale: 0.98 } : {}}
                  style={{
                    boxShadow: copyState === 'success' ? 
                      '0 0 20px rgba(16, 185, 129, 0.6)' : 
                      copyState === 'error' ? 
                      '0 0 20px rgba(239, 68, 68, 0.6)' : 
                      '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {/* 成功状态的背景脉冲效果 */}
                  {copyState === 'success' && (
                    <motion.div
                      className="absolute inset-0 bg-green-400 opacity-30 rounded-lg"
                      animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.3, 0.1, 0.3]
                      }}
                      transition={{
                        duration: 1,
                        repeat: 1
                      }}
                    />
                  )}
                  
                  <motion.i 
                    className={`mr-2 fas ${buttonConfig.icon} relative z-10`}
                    animate={{ 
                      scale: copyState === 'success' ? [1, 1.3, 1] : 1,
                      color: copyState === 'success' ? '#FFFFFF' : undefined
                    }}
                    transition={{ 
                      scale: { duration: 0.6, ease: "easeInOut" }
                    }}
                    style={{
                      animation: copyState === 'loading' ? 'spin 1s linear infinite' : undefined
                    }}
                  />
                  <motion.span
                    className="relative z-10"
                    animate={{ 
                      opacity: 1,
                      y: 0
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    {buttonConfig.text}
                  </motion.span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      
      {/* 支付弹窗 */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        itemType="prompt"
        itemId={prompt.id}
        itemName={prompt.title}
        price={prompt.price || 0}
        sellerName={prompt.author}
      />
    </AnimatePresence>
  );
};

const PromptPlazaCard: React.FC<PromptPlazaCardProps> = ({ prompt, onCopy, isAuthenticated, copyingStates, allPrompts = [] }) => {
  const [showUserCard, setShowUserCard] = useState(false);
  const [userCardPosition, setUserCardPosition] = useState({ x: 0, y: 0 });
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [userHasBio, setUserHasBio] = useState<boolean | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState(prompt); // 新增：当前显示的提示词
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPurchased, setIsPurchased] = useState(prompt.purchased || false);
  const copyState = copyingStates[prompt.id] || 'idle';

  // 检查购买状态
  useEffect(() => {
    const checkPurchaseStatus = async () => {
      // 只有需要付费的提示词才检查购买状态
      if ((prompt.price || 0) > 0 && isAuthenticated && !isPurchased) {
        try {
          const result = await paymentApi.checkPurchase('prompt', prompt.id);
          if (result.success && result.data?.purchased) {
            setIsPurchased(true);
          }
        } catch (error) {
          console.error('检查购买状态失败:', error);
        }
      }
    };
    checkPurchaseStatus();
  }, [prompt.id, prompt.price, isAuthenticated, isPurchased]);

  // 是否需要付费（有价格且未购买）
  const needPayment = (prompt.price || 0) > 0 && !isPurchased;

  const handleCopy = () => {
    if (!isAuthenticated) {
      toast.error('请先登录后再复制提示词');
      return;
    }
    // 如果需要付费且未购买，提示需要先购买
    if (needPayment) {
      toast.error('请先购买后再复制提示词');
      setShowPaymentModal(true);
      return;
    }
    onCopy(prompt);
  };

  const handleContentClick = () => {
    // 如果需要付费，则弹出支付弹窗
    if (needPayment && isAuthenticated) {
      setShowPaymentModal(true);
    } else if (needPayment && !isAuthenticated) {
      toast.error('请先登录后再购买');
    } else {
      setShowDetailModal(true);
    }
  };

  // 支付成功回调
  const handlePaymentSuccess = () => {
    setIsPurchased(true);
    setShowPaymentModal(false);
    setShowDetailModal(true);
  };



  // 处理用户名悬浮
  const handleUserNameHover = (e: React.MouseEvent) => {
    if (!prompt.userId) return;
    
    // 清除之前的定时器
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    setUserCardPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8
    });
    
    // 立即显示，不需要延迟
    setShowUserCard(true);
  };

  const handleUserNameLeave = () => {
    // 清除显示定时器
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    // 延迟隐藏，给用户时间移动到卡片上
    const timeout = setTimeout(() => {
      setShowUserCard(false);
    }, 500); // 增加延迟时间到500ms
    setHoverTimeout(timeout);
  };

  const handleUserCardClose = () => {
    setShowUserCard(false);
    // 清除任何待执行的定时器
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  // 当鼠标进入卡片时，取消关闭定时器
  const handleUserCardMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  // 当鼠标离开卡片时，延迟关闭
  const handleUserCardMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowUserCard(false);
    }, 300);
    setHoverTimeout(timeout);
  };

  // 获取复制按钮的样式和内容
  const getCopyButtonConfig = () => {
    if (!isAuthenticated) {
      return {
        className: 'bg-gray-100 text-gray-400 cursor-not-allowed',
        disabled: true,
        icon: 'fa-copy',
        text: '请先登录'
      };
    }
    
    // 如果需要付费，显示付费按钮
    if (needPayment) {
      return {
        className: 'bg-emerald-500 text-white hover:bg-emerald-600',
        disabled: false,
        icon: 'fa-lock',
        text: `¥${(prompt.price || 0).toFixed(2)}`
      };
    }

    switch (copyState) {
      case 'loading':
        return {
          className: 'bg-blue-500 text-white cursor-wait',
          disabled: true,
          icon: 'fa-spinner fa-spin',
          text: '复制中...'
        };
      case 'success':
        return {
          className: 'bg-green-500 text-white',
          disabled: true,
          icon: 'fa-check',
          text: '复制成功'
        };
      case 'error':
        return {
          className: 'bg-red-500 text-white hover:bg-red-600',
          disabled: false,
          icon: 'fa-exclamation-triangle',
          text: '复制失败，重试'
        };
      default:
        return {
          className: 'bg-primary text-white hover:bg-primary-dark',
          disabled: false,
          icon: 'fa-copy',
          text: '复制到我的提示词'
        };
    }
  };

  const buttonConfig = getCopyButtonConfig();

  // 清理定时器
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  // 检查用户是否有个人简介
  const checkUserBio = async (userId: number) => {
    if (!userId) {
      setUserHasBio(false);
      return;
    }
    
    try {
      const response = await userApi.getProfile(userId);
      if (response.success && response.data) {
        setUserHasBio(!!response.data.bio);
      } else {
        setUserHasBio(false);
      }
    } catch (error) {
      setUserHasBio(false);
    }
  };

  // 当组件加载时检查用户简介
  useEffect(() => {
    if (prompt.userId) {
      checkUserBio(prompt.userId);
    }
  }, [prompt.userId]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:shadow-primary/20 transition-all duration-300"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2 leading-tight whitespace-pre-wrap flex-1 pr-4">
                {prompt.title}
              </h3>
              <div className="flex items-center space-x-2 flex-shrink-0">
                {/* 价格徽章 */}
                {prompt.price !== undefined && prompt.price > 0 ? (
                  isPurchased ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                      <i className="fas fa-check-circle text-blue-600 dark:text-blue-400 mr-1"></i>
                      已购买
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                      <i className="fas fa-tag text-emerald-600 dark:text-emerald-400 mr-1"></i>
                      ¥{prompt.price.toFixed(2)}
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                    <i className="fas fa-gift text-green-600 dark:text-green-400 mr-1"></i>
                    免费
                  </span>
                )}
                {/* 热度徽章 */}
                {prompt.hotness !== undefined && prompt.hotness > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                    <i className="fas fa-fire text-orange-600 dark:text-orange-400 mr-1"></i>
                    {prompt.hotness}
                  </span>
                )}
              </div>
            </div>
            
            {/* Tags紧跟在标题下方 */}
            {prompt.tags && prompt.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {prompt.tags.map((tag, index) => (
                  <span
                    key={`${prompt.id}-tag-${index}-${tag}`}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
            
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
              <i className="fas fa-user text-gray-400 dark:text-gray-500 mr-2"></i>
              {prompt.userId ? (
                <span
                  className={`${userHasBio ? 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'} cursor-pointer font-medium transition-colors`}
                  onMouseEnter={handleUserNameHover}
                  onMouseLeave={handleUserNameLeave}
                >
                  {prompt.author || '匿名用户'}
                </span>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">{prompt.author || '匿名用户'}</span>
              )}
              <span className="mx-2">•</span>
              <span>{new Date(prompt.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <div 
          className="mb-4 relative cursor-pointer" 
          onClick={handleContentClick}
        >
          {/* 付费内容模糊处理 */}
          {needPayment ? (
            <div className="relative">
              {/* 付费提示词显示介绍内容 */}
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap line-clamp-5">
                {prompt.description || '暂无介绍'}
              </p>
              <div className="mt-3 flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-flex items-center px-4 py-2 bg-emerald-500 text-white rounded-full text-sm font-medium shadow-lg hover:bg-emerald-600 transition-colors">
                    <i className="fas fa-lock mr-2"></i>
                    支付 ¥{(prompt.price || 0).toFixed(2)} 解锁完整内容
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap line-clamp-5">
                {prompt.content}
              </p>
              
              {/* 查看详情按钮 */}
              {prompt.content.length > 200 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContentClick();
                  }}
                  className="text-primary hover:text-primary-dark text-sm mt-2 font-medium transition-colors flex items-center"
                >
                  <i className="fas fa-eye mr-1"></i>
                  查看完整内容
                </button>
              )}
            </>
          )}
        </div>

        {/* 效果图片预览 */}
        {prompt.images && prompt.images.length > 0 && (
          <div className="flex gap-2 mb-4 overflow-x-auto">
            {prompt.images.slice(0, 4).map((imageUrl, index) => (
              <ImageHoverPreview
                key={index}
                imageUrl={imageUrl}
                alt={`效果图 ${index + 1}`}
                thumbnailClassName="w-14 h-14"
                previewSize={320}
              />
            ))}
            {prompt.images.length > 4 && (
              <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-xs font-medium">
                +{prompt.images.length - 4}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            分类: {prompt.category || '未分类'}
          </div>
          <div className="flex items-center space-x-2">
            <motion.button
              onClick={handleCopy}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 relative overflow-hidden ${buttonConfig.className}`}
              disabled={buttonConfig.disabled}
              whileHover={!buttonConfig.disabled ? { scale: 1.02 } : {}}
              whileTap={!buttonConfig.disabled ? { scale: 0.98 } : {}}
              style={{
                boxShadow: copyState === 'success' ? 
                  '0 0 20px rgba(16, 185, 129, 0.6)' : 
                  copyState === 'error' ? 
                  '0 0 20px rgba(239, 68, 68, 0.6)' : 
                  '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
              transition={{ duration: 0.3 }}
            >
              {/* 成功状态的背景脉冲效果 */}
              {copyState === 'success' && (
                <motion.div
                  className="absolute inset-0 bg-green-400 opacity-30 rounded-lg"
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.1, 0.3]
                  }}
                  transition={{
                    duration: 1,
                    repeat: 1
                  }}
                />
              )}
              
              <motion.i 
                className={`mr-2 fas ${buttonConfig.icon} relative z-10`}
                animate={{ 
                  scale: copyState === 'success' ? [1, 1.3, 1] : 1,
                  color: copyState === 'success' ? '#FFFFFF' : undefined
                }}
                transition={{ 
                  scale: { duration: 0.6, ease: "easeInOut" }
                }}
                style={{
                  animation: copyState === 'loading' ? 'spin 1s linear infinite' : undefined
                }}
              />
              <motion.span
                className="relative z-10"
                animate={{ 
                  opacity: 1,
                  y: 0
                }}
                transition={{ duration: 0.2 }}
              >
                {buttonConfig.text}
              </motion.span>
            </motion.button>
          </div>
        </div>
      </motion.div>
      
      {/* 提示词详情弹框 */}
      <PromptDetailModal
        prompt={currentPrompt}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onCopy={onCopy}
        isAuthenticated={isAuthenticated}
        copyingStates={copyingStates}
        allPrompts={allPrompts}
        onPromptSelect={(newPrompt) => {
          setCurrentPrompt(newPrompt);
        }}
      />
      
      {/* 用户资料卡片 */}
      {prompt.userId && (
        <UserCard
          userId={prompt.userId}
          username={prompt.author || '匿名用户'}
          isVisible={showUserCard}
          position={userCardPosition}
          onClose={handleUserCardClose}
          onMouseEnter={handleUserCardMouseEnter}
          onMouseLeave={handleUserCardMouseLeave}
        />
      )}
      
      {/* 支付弹窗 */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        itemType="prompt"
        itemId={prompt.id}
        itemName={prompt.title}
        price={prompt.price || 0}
        sellerName={prompt.author}
      />
    </>
  );
};

const PromptPlaza: React.FC<PromptPlazaProps> = ({ onPromptCopied }) => {
  const { isAuthenticated } = useAuth();
  const [publicPrompts, setPublicPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<'created_at' | 'hotness'>('created_at');
  const limit = 12;
  const [copyingStates, setCopyingStates] = useState<Record<number, 'idle' | 'loading' | 'success' | 'error'>>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // 加载公共提示词
  const loadPublicPrompts = useCallback(async (isLoadMore = false, currentOffset?: number) => {
    try {
      setIsLoading(true);
      
      // 确定要使用的offset
      const offsetToUse = isLoadMore ? (currentOffset ?? 0) : 0;
      
      const response = await publicPromptsApi.getPublicPrompts({
        search: searchQuery || undefined,
        category: selectedCategory === '全部' ? undefined : selectedCategory,
        limit,
        offset: offsetToUse,
        sortBy
      });

      if (response.success && response.data) {
        const prompts = response.data;
        if (isLoadMore) {
          // 添加去重逻辑，防止重复数据导致key冲突
          setPublicPrompts(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            const newPrompts = prompts.filter(p => !existingIds.has(p.id));
            return [...prev, ...newPrompts];
          });
        } else {
          setPublicPrompts(prompts);
        }
        
        setHasMore(prompts.length === limit);
        setOffset(offsetToUse + prompts.length);
      }
    } catch (error) {
      const message = handleApiError(error);
      toast.error(`加载提示词广场失败: ${message}`);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedCategory, limit, sortBy]); // 添加selectedCategory依赖

  // 搜索处理
  const handleSearch = useCallback(async (query: string) => {
    try {
      setIsLoading(true);
      setOffset(0);
      setPublicPrompts([]);
      
      const response = await publicPromptsApi.getPublicPrompts({
        search: query || undefined,
        category: selectedCategory === '全部' ? undefined : selectedCategory,
        limit,
        offset: 0,
        sortBy
      });

      if (response.success && response.data) {
        setPublicPrompts(response.data);
        setHasMore(response.data.length === limit);
        setOffset(response.data.length);
      }
    } catch (error) {
      const message = handleApiError(error);
      toast.error(`搜索失败: ${message}`);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, limit, sortBy]); // 添加selectedCategory依赖

  // 类别筛选处理
  const handleCategoryChange = useCallback(async (category: string) => {
    if (category === selectedCategory) return; // 如果是相同类别则不执行
    
    setSelectedCategory(category);
    setOffset(0);
    setPublicPrompts([]);
    
    try {
      setIsLoading(true);
      const response = await publicPromptsApi.getPublicPrompts({
        search: searchQuery || undefined,
        category: category === '全部' ? undefined : category,
        limit,
        offset: 0,
        sortBy
      });

      if (response.success && response.data) {
        setPublicPrompts(response.data);
        setHasMore(response.data.length === limit);
        setOffset(response.data.length);
      }
    } catch (error) {
      const message = handleApiError(error);
      toast.error(`筛选失败: ${message}`);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedCategory, limit, sortBy]);

  // 排序切换处理
  const handleSortChange = useCallback(async (newSortBy: 'created_at' | 'hotness') => {
    if (newSortBy === sortBy) return; // 如果是相同排序则不执行
    
    setSortBy(newSortBy);
    setOffset(0);
    setPublicPrompts([]);
    
    try {
      setIsLoading(true);
      const response = await publicPromptsApi.getPublicPrompts({
        search: searchQuery || undefined,
        category: selectedCategory === '全部' ? undefined : selectedCategory,
        limit,
        offset: 0,
        sortBy: newSortBy
      });

      if (response.success && response.data) {
        setPublicPrompts(response.data);
        setHasMore(response.data.length === limit);
        setOffset(response.data.length);
      }
    } catch (error) {
      const message = handleApiError(error);
      toast.error(`加载失败: ${message}`);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedCategory, limit, sortBy]);

  // 复制提示词
  const handleCopyPrompt = async (prompt: Prompt) => {
    if (!isAuthenticated) {
      toast.error('请先登录后再复制提示词');
      return;
    }

    // 设置loading状态
    setCopyingStates(prev => ({ ...prev, [prompt.id]: 'loading' }));

    try {
      const response = await publicPromptsApi.copyPrompt(prompt.id);
      if (response.success) {
        toast.success(
          <div className="flex items-center">
            <i className="fas fa-check-circle text-green-500 mr-2"></i>
            <span>提示词复制成功！已添加到您的提示词列表</span>
          </div>,
          {
            duration: 3000,
            style: {
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              color: '#166534'
            }
          }
        );
        // 通知父组件刷新提示词列表
        if (onPromptCopied) {
          onPromptCopied();
        }
        // 设置成功状态
        setCopyingStates(prev => ({ ...prev, [prompt.id]: 'success' }));
        
        // 2秒后重置状态
        setTimeout(() => {
          setCopyingStates(prev => ({ ...prev, [prompt.id]: 'idle' }));
        }, 2000);
      }
    } catch (error) {
      const message = handleApiError(error);
      toast.error(
        <div className="flex items-center">
          <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
          <span>复制失败: {message}</span>
        </div>,
        {
          duration: 4000,
          style: {
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b'
          }
        }
      );
      // 设置错误状态
      setCopyingStates(prev => ({ ...prev, [prompt.id]: 'error' }));
      
      // 3秒后重置状态
      setTimeout(() => {
        setCopyingStates(prev => ({ ...prev, [prompt.id]: 'idle' }));
      }, 3000);
    }
  };

  // 加载更多
  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      loadPublicPrompts(true, offset);
    }
  };

  // 初始加载
  useEffect(() => {
    const initialLoad = async () => {
      try {
        setIsLoading(true);
        setOffset(0);
        setPublicPrompts([]);
        
        const response = await publicPromptsApi.getPublicPrompts({
          search: undefined,
          category: selectedCategory === '全部' ? undefined : selectedCategory,
          limit,
          offset: 0,
          sortBy
        });

        if (response.success && response.data) {
          setPublicPrompts(response.data);
          setHasMore(response.data.length === limit);
          setOffset(response.data.length);
        }
      } catch (error) {
        const message = handleApiError(error);
        toast.error(`加载提示词广场失败: ${message}`);
      } finally {
        setIsLoading(false);
      }
    };

    initialLoad();
  }, [selectedCategory, sortBy, refreshTrigger]); // 添加refreshTrigger依赖

  // 监听缓存清除事件，用于提示词状态变化后的自动刷新
  useEffect(() => {
    const handleCacheCleared = () => {
      console.log('🔄 检测到缓存清除，刷新提示词广场');
      setRefreshTrigger(prev => prev + 1);
    };

    const handlePromptStatusChanged = (event: CustomEvent) => {
      console.log('🔄 检测到提示词状态变化，强制刷新提示词广场', event.detail);
      // 强制刷新，清空当前数据并重新加载
      setPublicPrompts([]);
      setOffset(0);
      setRefreshTrigger(prev => prev + 1);
    };

    // 监听自定义的缓存清除事件
    window.addEventListener('api-cache-cleared', handleCacheCleared);
    window.addEventListener('prompt-status-changed', handlePromptStatusChanged as EventListener);
    
    return () => {
      window.removeEventListener('api-cache-cleared', handleCacheCleared);
      window.removeEventListener('prompt-status-changed', handlePromptStatusChanged as EventListener);
    };
  }, []);

  // 搜索防抖
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery !== '') {
        handleSearch(searchQuery);
      } else {
        // 如果搜索为空，重新加载所有数据
        handleSearch('');
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, handleSearch]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-8 transition-colors duration-200">
      {/* 头部 */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
        <div className="container mx-auto px-6 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              <i className="fas fa-globe text-primary mr-3"></i>
              AI提示词广场 - 发现优质提示词管理方案
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              发现和分享优质的AI提示词，让创作更高效
            </p>

            {/* 搜索栏 */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="搜索公共提示词..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-12 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                />
                <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"></i>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                    title="清除搜索"
                    aria-label="清除搜索"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {!isAuthenticated && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg max-w-2xl mx-auto">
                <p className="text-blue-800 dark:text-blue-200">
                  <i className="fas fa-info-circle mr-2"></i>
                  您可以浏览和查看所有公共提示词，登录后即可复制到您的提示词库
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* 移动端筛选按钮 */}
      <div className="lg:hidden container mx-auto px-6 py-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          {/* 类别筛选 - 移动端水平滚动 */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">分类</h4>
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex gap-2 min-w-max">
                {['全部', ...PROMPT_CATEGORIES].map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                      selectedCategory === category
                        ? 'bg-primary text-white shadow-lg'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {category === '全部' ? (
                      <>
                        <i className="fas fa-th-large mr-1"></i>
                        全部
                      </>
                    ) : (
                      <>
                        <i className="fas fa-folder mr-1"></i>
                        {category}
                      </>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 排序选项 - 移动端 */}
          <div className="flex gap-2">
            <button
              onClick={() => handleSortChange('created_at')}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                sortBy === 'created_at' 
                  ? 'bg-primary text-white shadow-sm' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <i className="fas fa-clock mr-1"></i>
              最新
            </button>
            <button
              onClick={() => handleSortChange('hotness')}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                sortBy === 'hotness' 
                  ? 'bg-primary text-white shadow-sm' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <i className="fas fa-fire mr-1"></i>
              最热
            </button>
          </div>
        </div>
      </div>

      {/* 主要内容区域 - 左侧边栏 + 右侧内容 */}
      <div className="container mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* 左侧筛选边栏 - 仅桌面端显示 */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                <i className="fas fa-filter text-primary mr-2"></i>
                筛选条件
              </h3>
              
              {/* 类别筛选 */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">分类</h4>
                <div className="space-y-2">
                  {['全部', ...PROMPT_CATEGORIES].map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryChange(category)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedCategory === category
                          ? 'bg-primary text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      {category === '全部' ? (
                        <>
                          <i className="fas fa-th-large mr-2 w-4"></i>
                          全部
                        </>
                      ) : (
                        <>
                          <i className="fas fa-folder mr-2 w-4"></i>
                          {category}
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* 排序选项 */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">排序方式</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => handleSortChange('created_at')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      sortBy === 'created_at' 
                        ? 'bg-primary text-white shadow-sm' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <i className="fas fa-clock mr-2 w-4"></i>
                    最新发布
                  </button>
                  <button
                    onClick={() => handleSortChange('hotness')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      sortBy === 'hotness' 
                        ? 'bg-primary text-white shadow-sm' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <i className="fas fa-fire mr-2 w-4"></i>
                    最热门
                  </button>
                </div>
              </div>

              {/* 清除筛选 */}
              {(selectedCategory !== '全部' || searchQuery) && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('全部');
                    }}
                    className="w-full px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <i className="fas fa-times mr-2"></i>
                    清除所有筛选
                  </button>
                </div>
              )}
            </div>

            {/* Google 广告区域 */}
            <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs text-gray-400 dark:text-gray-500 mb-2 text-center">广告</div>
              <GoogleAdBanner 
                format="vertical"
                className="rounded-lg overflow-hidden"
              />
            </div>
          </div>

          {/* 内容区域 - 移动端全宽，桌面端右侧 */}
          <div className="w-full lg:flex-1 lg:min-w-0">
            {isLoading && offset === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400 font-medium">正在加载提示词广场...</p>
                </div>
              </div>
            ) : publicPrompts.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {publicPrompts.map((prompt) => (
                    <PromptPlazaCard
                      key={prompt.id}
                      prompt={prompt}
                      onCopy={handleCopyPrompt}
                      isAuthenticated={isAuthenticated}
                      copyingStates={copyingStates}
                      allPrompts={publicPrompts}
                    />
                  ))}
                </div>

                {/* 推广广告 */}
                <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 overflow-hidden">
                  <a href="https://z-pay.cn/?uid=18240" target="_blank" rel="noopener noreferrer">
                    <img 
                      src="https://member.z-pay.cn/member/images/banner1.png" 
                      alt="推广" 
                      className="w-full rounded-lg hover:opacity-90 transition-opacity"
                    />
                  </a>
                </div>

                {/* 加载更多按钮 */}
                {hasMore && (
                  <div className="text-center mt-8">
                    <button
                      onClick={handleLoadMore}
                      disabled={isLoading}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          加载中...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus mr-2"></i>
                          加载更多
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <i className="fas fa-search text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {searchQuery || selectedCategory !== '全部' ? '未找到相关提示词' : '暂无公共提示词'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {searchQuery || selectedCategory !== '全部'
                      ? '尝试调整搜索条件或筛选类别'
                      : '还没有用户分享提示词到广场，成为第一个分享者吧！'
                    }
                  </p>
                  {(searchQuery || selectedCategory !== '全部') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('全部');
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <i className="fas fa-times mr-2"></i>
                      清除所有筛选
                    </button>
                  )}
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptPlaza; 